import { useState, useEffect } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { Menu, X, Twitter, ExternalLink, Zap } from 'lucide-react'
import clsx from 'clsx'
import { useCollectionStats } from '../hooks/useCollectionStats'

const navLinks = [
  { to: '/',         label: 'Home'          },
  { to: '/games',    label: 'Games'         },
  { to: '/facts',    label: '10K Facts'     },
  { to: '/identity', label: 'Identity Mint' },
  { to: '/score',  label: '10kScore'   },
  { to: '/tokens',   label: 'Launch'        },
  { to: '/boti',     label: '🦜 Boti'       },
  { to: '/arena',    label: 'Squad Arena'   },
  { to: '/squad-checker', label: 'Xrate' }
]

// ─── Hoisted NavLink classname functions (avoids TS arrow-fn-in-JSX bug) ─────

function navClass({ isActive }: { isActive: boolean }): string {
  return clsx(
    'font-body font-medium text-sm tracking-wide transition-all duration-200 relative py-1',
    isActive
      ? 'text-pink-400 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-pink-500'
      : 'text-pink-200/60 hover:text-pink-300'
  )
}

function mobileNavClass({ isActive }: { isActive: boolean }): string {
  return clsx(
    'font-body font-medium text-base py-3 px-4 rounded-xl transition-all',
    isActive
      ? 'bg-pink-600/20 text-pink-300 border border-pink-600/30'
      : 'text-pink-200/60 hover:bg-pink-900/20 hover:text-pink-300'
  )
}

// ─── Ticker helpers ───────────────────────────────────────────────────────────

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function sign(n: number): string {
  return n >= 0 ? `+${n.toFixed(1)}` : n.toFixed(1)
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Layout() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  const stats = useCollectionStats('the-10k-squad-350905768', 60_000)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => { setMenuOpen(false) }, [location.pathname])

  // ─── Derived ticker values ──────────────────────────────────────────────────

  const floorLabel   = stats.loading ? '—' : `${fmt(stats.floor)} MON`
  const volumeLabel  = stats.loading ? '—' : `${fmt(stats.volume24h)} ETH`
  const holdersLabel = stats.loading ? '—' : fmt(stats.holders)
  const supplyLabel  = stats.loading ? '—' : fmt(stats.supply)
  const changeLabel  = stats.loading ? '—' : `${sign(stats.floorChange)}%`
  const changeColor  = stats.floorChange >= 0 ? 'text-green-400' : 'text-red-400'
  const changeArrow  = stats.floorChange >= 0 ? '↑' : '↓'

  const tickerItems = [
    <span key="floor"   className="flex items-center gap-2 px-6"><Zap size={10} className="text-yellow-400" /> FLOOR: {floorLabel}</span>,
    <span key="volume"  className="flex items-center gap-2 px-6"><span className={changeColor}>{changeArrow}</span> 24H VOLUME: {volumeLabel}</span>,
    <span key="holders" className="flex items-center gap-2 px-6">👥 HOLDERS: {holdersLabel}</span>,
    <span key="supply"  className="flex items-center gap-2 px-6">📦 SUPPLY: {supplyLabel}</span>,
    <span key="change"  className="flex items-center gap-2 px-6"><span className={changeColor}>{changeArrow}</span> FLOOR 1D: {changeLabel}</span>,
    <span key="chain"   className="flex items-center gap-2 px-6">🔗 ON MONAD</span>,
  ]

  return (
    <div className="min-h-dvh flex flex-col bg-[#0a0008]">

      {/* ── NAVBAR ── */}
      <header
        className={clsx(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
          scrolled
            ? 'bg-[#0a0008]/90 backdrop-blur-md border-b border-pink-900/30'
            : 'bg-transparent'
        )}
      >
        {/* Live ticker */}
        <div className="bg-pink-600/10 border-b border-pink-600/20 ticker-wrap py-1">
          <div className="ticker-inner gap-12 text-xs font-mono text-pink-300">
            {[0, 1].flatMap(i =>
              tickerItems.map(item => ({ ...item, key: `${i}-${item.key}` }))
            )}
          </div>
        </div>

        <nav className="container mx-auto px-4 h-14 flex items-center justify-between">
          {/* Logo */}
          <NavLink to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-pink-600 flex items-center justify-center font-display text-white text-lg leading-none glow-border">
              S
            </div>
            <span className="font-display text-xl text-white tracking-widest group-hover:text-pink-300 transition-colors">
              10KSQUADBOT
            </span>
          </NavLink>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {navLinks.map(({ to, label }) => (
              <NavLink key={to} to={to} end={to === '/'} className={navClass}>
                {label}
              </NavLink>
            ))}
            <a
              href="https://x.com/10ksquadbot"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost !py-2 !px-4 !text-xs flex items-center gap-2"
            >
              <Twitter size={13} /> @10ksquadbot
            </a>
            <a
              href="https://opensea.io/collection/the-10k-squad-350905768"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pink !py-2 !px-4 !text-xs flex items-center gap-2"
            >
              Buy on OpenSea <ExternalLink size={11} />
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden text-pink-300 p-2 hover:text-pink-100 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </nav>

        {/* Mobile menu */}
        <div
          className={clsx(
            'md:hidden transition-all duration-300 overflow-hidden bg-[#12000f]/95 backdrop-blur-xl border-b border-pink-900/30',
            menuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="px-4 py-4 flex flex-col gap-2">
            {navLinks.map(({ to, label }) => (
              <NavLink key={to} to={to} end={to === '/'} className={mobileNavClass}>
                {label}
              </NavLink>
            ))}
            <div className="h-px bg-pink-900/30 my-1" />
            <a
              href="https://x.com/10ksquadbot"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 py-3 px-4 text-pink-300 text-sm font-body rounded-xl hover:bg-pink-900/20 transition-all"
            >
              <Twitter size={15} /> @10ksquadbot on X
            </a>
            <a
              href="https://opensea.io/collection/the-10k-squad-350905768"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pink w-full text-center flex items-center justify-center gap-2 mt-1"
            >
              Buy on OpenSea <ExternalLink size={13} />
            </a>
          </div>
        </div>
      </header>

      {/* ── PAGE CONTENT ── */}
      <main className="flex-1 pt-[88px]">
        <Outlet />
      </main>

      {/* ── FOOTER ── */}
      <footer className="border-t border-pink-900/30 bg-[#12000f] mt-20">
        <div className="container mx-auto px-4 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col items-center md:items-start gap-1">
              <span className="font-display text-2xl text-white tracking-widest">10KSQUADBOT</span>
              <span className="text-xs text-pink-400/60 font-mono">
                Real-time NFT sales tracker · Monad blockchain
                {stats.lastUpdated && (
                  <> · Updated {new Date(stats.lastUpdated).toLocaleTimeString()}</>
                )}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="https://x.com/10ksquadbot"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl border border-pink-600/40 flex items-center justify-center text-pink-400 hover:text-white hover:border-pink-500 hover:bg-pink-600/20 transition-all"
              >
                <Twitter size={15} />
              </a>
              <a
                href="https://opensea.io/collection/the-10k-squad-350905768"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-xl border border-pink-600/40 flex items-center justify-center text-pink-400 hover:text-white hover:border-pink-500 hover:bg-pink-600/20 transition-all"
              >
                <ExternalLink size={15} />
              </a>
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-pink-900/20 text-center text-xs text-pink-900 font-mono">
            © {new Date().getFullYear()} 10KSquadBot · Not financial advice · Built on Monad
          </div>
        </div>
      </footer>

    </div>
  )
}